import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as Yup from 'yup';
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Paper,
  TextField,
  InputAdornment
} from '@material-ui/core';
import ReceiptIcon from '@material-ui/icons/Receipt';
import { useFormik, Form, FormikProvider } from 'formik';
import moment from 'moment';
import { baseUrl, controlAccounts } from '../../utils/constants';
import { useClientContext } from '../../context/client_context';
import { useAccountingContext } from '../../context/accounting_context';
import Controls from '../controls/Controls';

export default function Adjustments() {
  const dateToday = new Date();
  const navigate = useNavigate();
  const { refId, setRefId } = useAccountingContext();
  const [adjustment, setAdjustment] = useState({
    partyAccount: refId,
    controlAccount: 0,
    amount: 0,
    reason: '',
    accountType: 0,
    entryType: 0,
    ledgerDate: '',
    dateProcessed: moment(dateToday).format('yyyy-MM-DD'),
    userRefId: sessionStorage.getItem('oldRefId')
  });

  const [accountName, setAccountName] = useState('');
  const [balance, setBalance] = useState('');
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: 'success' });

  const creditDebitTypeList = [
    { refId: 2, name: 'Select Type (Debit or Credit)' },
    { refId: 0, name: 'Debit' },
    { refId: 1, name: 'Credit' }
  ];

  useEffect(() => {
    console.log('refId here', refId);
    if (refId > 0) getAccountInfo(refId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const AdjustmentSchema = Yup.object().shape({
    amount: Yup.number()
      .required('you must specify the Adjustment Amount')
      .min(1, 'Adjustment Amount should be greater than 1'),
    entryType: Yup.number()
      .required('Please select Credit/Debit indicator')
      .max(1, 'Invalid option selected'),
    reason: Yup.string().required('Reason for Adjustment is Required'),
    ledgerDate: Yup.date().required('Please enter the Ledger Date'),
    controlAccount: Yup.number()
      .required('you must specify the Control Account')
      .min(1, 'Control Account not specified')
  });

  const formik = useFormik({
    initialValues: adjustment,
    enableReinitialize: true,
    validationSchema: AdjustmentSchema,
    onSubmit: (values) => {
      console.log('values b4', values);
      saveAdjustment(values);
    }
  });

  const getAccountInfo = async (ref) => {
    await axios(
      `${baseUrl}/api/accounting/getAccountBalance?accountRefId=${ref}&accountType=1`
    ).then(
      (result) => {
        setLoading(false);
        if (result.replyStatus === 1) {
          notifyPopup(`Error in Getting AccountInfo!: ${result.replyMessage}`, 'error');
        } else {
          formik.setFieldValue('accountRefId', result.data.accountNumber);
          setAccountName(result.data.accountName);
          setBalance(result.data.balance);
        }
      },
      (error) => {
        notifyPopup(`Error in Getting ClientInfo!: ${error}`, 'error');
      }
    );
  };

  const saveAdjustment = async (values) => {
    console.log('values to save', values);
    axios.post(`${baseUrl}/api/accounting/postGeneralLedger`, values).then(
      (result) => {
        setLoading(false);
        if (result.replyStatus === 1) {
          notifyPopup(`Error in Saving Adjustment!: ${result.replyMessage}`, 'error');
        } else {
          notifyPopup('Adjustment Saved Successfully', 'success');
          navigate(-1);
        }
      },
      (error) => {
        notifyPopup(`Error in Saving Adjustment!: ${error}`, 'error');
      }
    );
  };

  const notifyPopup = (notifyMessage, notifyType) => {
    setNotify({
      isOpen: true,
      message: `${notifyMessage}`,
      type: `${notifyType}`
    });
  };

  return (
    <>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate>
          <Typography component="span" variant="h6" gutterBottom>
            Adjustment Transaction
          </Typography>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={8} sm={8} spacing={2}>
                  <Grid item xs={12} sm={12} m={2}>
                    <Controls.SelectJson
                      style={{ width: '40%' }}
                      name="controlAccount"
                      label="Contra Entry Type"
                      defaultValue=""
                      options={controlAccounts}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} m={2}>
                    <Controls.SelectJson
                      style={{ width: '40%' }}
                      name="entryType"
                      label="Credit/Debit"
                      options={creditDebitTypeList}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} m={2}>
                    <Controls.InputNumber
                      style={{ width: '40%' }}
                      name="amount"
                      label="Transaction Amount"
                      disabled={formik.values.isInstallment}
                    />
                  </Grid>
                  <Grid container spacing={2} sx={{ m: 2, ml: 0 }}>
                    <Grid item xs={12} sm={4}>
                      <Controls.DateTime name="ledgerDate" label="Ledger Date" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controls.Input name="reason" label="Adjustment Reason" />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ m: 2, ml: 0 }}>
                    <Grid item xs={2} sm={2}>
                      <Button
                        type="submit"
                        variant="outlined"
                        color="secondary"
                        startIcon={<ReceiptIcon />}
                      >
                        Process
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={4} mt={2}>
                  <Paper variant="outlined">
                    <Grid item xs={12} sm={12} margin={1}>
                      <Controls.InputNumber name="partyAccount" label="Account #" disabled />
                    </Grid>
                    <Grid item xs={12} sm={12} margin={1}>
                      <TextField
                        size="small"
                        id="accName"
                        fullWidth
                        label="Account Name"
                        inputProps={{ style: { textAlign: 'left' } }}
                        value={accountName}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} margin={1}>
                      <TextField
                        size="small"
                        id="balance"
                        helperText="Current Balance"
                        InputProps={{
                          inputComponent: Controls.NumberDecimal,
                          endAdornment: (
                            <InputAdornment position="end">
                              {balance < 0 ? <b>CR</b> : <b>DR</b>}
                            </InputAdornment>
                          ),
                          style: { textAlign: 'right' }
                        }}
                        value={balance}
                      />
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>

              <Controls.Notification notify={notify} setNotify={setNotify} />
            </CardContent>
          </Card>
        </Form>
      </FormikProvider>
    </>
  );
}
